import React from 'react';
import { Field, FieldArray, change } from 'redux-form';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { UnitType } from '../../../../utils/enums';
import {
  AddItemButton,
  Container,
  ItemsContainer,
  PageDescription,
  PageHeader,
  Summary,
  SummaryContainer,
  SummaryLabel,
  SummaryValue,
  Title,
  TitleContainer,
  ToggleContainer,
} from './css';
import InlineTextLink from '../../../../lib/ui/buttons/InlineTextLink';
import ToggleButton from '../../../../lib/ui/buttons/ToggleButton';
import Item from './Item';
import { calculateVolumeDensity } from '../../../../utils/func';

const renderItemsForm = ({
  fields,
  measurementList,
  formName,
  dispatch,
  formData,
  commoditiesList,
}) => {
  const disableAddLineButton = measurementList && measurementList.some((m) => !m.completed);

  const addItem = (e) => {
    e.preventDefault();
    fields.push({});
  };

  const removeItem = (index) => {
    if (measurementList.length === 1) {
      dispatch(change(formName, 'measurementList', [{}]));
      return;
    }

    const newList = measurementList.filter((_, i) => i !== index);
    dispatch(
      change(
        formName,
        'measurementList',
        newList.length === 0 ? newList.push({}) : newList,
      ),
    );
  };

  return (
    <>
      {fields.map((item, index) => {
        const fieldValue = fields.get(index);
        dispatch(change(formName, `measurementList[${index}].completed`, true));

        const currentItem = formData.measurementList[index] || fieldValue;
        const {
          itemDangerous,
          commodityType,
          itemWeight,
          itemWidth,
          itemHeight,
          itemLength,
        } = currentItem;
        const isImperialUnits = formData.selectedUnits
          && formData.selectedUnits.indexOf(t('BtnImperial')) !== -1;
        const density = calculateVolumeDensity(
          isImperialUnits,
          itemLength,
          itemWidth,
          itemHeight,
          itemWeight,
        );

        return (
          <Item
            item={item}
            index={index}
            key={index}
            density={density}
            onRemove={removeItem}
            itemDangerous={itemDangerous}
            isImperialUnits={isImperialUnits}
            selectedUnits={formData.selectedUnits}
            commoditiesList={commoditiesList}
            usShipment={formData.USShipment}
            measurementList={formData.measurementList[index]}
            commodityType={commodityType}
          />
        );
      })}
      <AddItemButton disabled={disableAddLineButton} onClick={addItem}>
        {t('AddItem')}
      </AddItemButton>
    </>
  );
};

const Step2 = ({
  formData = {},
  step,
  formName,
  dispatch,
  valid,
  error,
  commoditiesList,
  unitsList,
}) => {
  const { measurementList } = formData;

  const setMeasurementUnit = (index) => {
    dispatch(change(formName, 'measurementType', unitsList[index]));
  };

  const totalPallets = measurementList && measurementList.length > 0
    ? measurementList.reduce((acc, curr) => (curr.SkidQty ? Number(acc) + Number(curr.SkidQty) : acc), 0)
    : 0;
  const totalPieces = measurementList && measurementList.length > 0
    ? measurementList.reduce((acc, curr) => (curr.PieceQty ? Number(acc) + Number(curr.PieceQty) : acc), 0)
    : 0;

  const totalItens = measurementList && measurementList.length;

  const totalWeight = (measurementList && measurementList.length > 0
    ? measurementList
      .map((item) => parseFloat(item.itemWeight))
      .reduce((acc, curr) => acc + (curr || 0), 0)
    : 0
  ).toFixed(2);

  if (step === 1) {
    return (
      <Container>
        <PageHeader>
          <TitleContainer>
            <Title>{t('Items')}</Title>
            <PageDescription>
              <span>
                {t('MeasurementsParagraph1')}
                {' '}
              </span>
              <InlineTextLink
                href={t('RestrictedArticlesLink')}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('RestrictedArticlesText')}
              </InlineTextLink>
              {t('MeasurementsParagraph2') !== 'MeasurementsParagraph2' && (
              <span>
                {' '}
                {t('MeasurementsParagraph2')}
                .
              </span>
              )}
            </PageDescription>
          </TitleContainer>
          <ToggleContainer>
            <Field
              name="selectedUnits"
              component={ToggleButton}
              label1={t(UnitType.Imperial)}
              label2={t(UnitType.Metric)}
              callback={setMeasurementUnit}
              disabled={totalItens > 1}
            />
          </ToggleContainer>
        </PageHeader>
        <ItemsContainer>
          <FieldArray
            name="measurementList"
            dispatch={dispatch}
            valid={valid}
            error={error}
            formData={formData}
            formName={formName}
            measurementList={measurementList}
            component={renderItemsForm}
            commoditiesList={commoditiesList}
          />
        </ItemsContainer>

        <SummaryContainer>
          <Summary>
            <SummaryLabel>
              {t('Total')}
              :
            </SummaryLabel>
            <SummaryValue>
              {totalPallets}
              {' '}
              {totalPallets === 1 ? t('Pallet') : t('Pallets')}
              {' '}
              {totalPieces}
              {' '}
              {totalPieces === 1 ? t('Piece') : t('Pieces')}
            </SummaryValue>
          </Summary>

          <Summary>
            <SummaryLabel>
              {t('TotalWeight')}
              :
            </SummaryLabel>
            <SummaryValue>
              {totalWeight}
              {' '}
              {formData.selectedUnits === t(UnitType.Imperial) ? 'lb' : 'kg'}
            </SummaryValue>
          </Summary>
        </SummaryContainer>
      </Container>
    );
  }

  return null;
};

Step2.propTypes = {
  formData: PropTypes.object,
  step: PropTypes.number,
  formName: PropTypes.string,
  dispatch: PropTypes.func,
  valid: PropTypes.bool,
  error: PropTypes.object,
  commoditiesList: PropTypes.object,
  unitsList: PropTypes.array,
};

export default Step2;
